import { HoppCollection } from "@hoppscotch/data"

/** A canonical egress boundary: secret values are local-only; public current values remain portable. */
export const stripSecretVariableValuesForWire = <
  T extends Record<string, unknown>,
>(
  variables: T[]
): T[] =>
  variables.map((variable) => {
    if (variable.secret !== true) return { ...variable }
    return { ...variable, initialValue: "", currentValue: "" }
  })

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)

/** Recursively strips variable values from a collection tree without mutating the UI state. */
export const stripCollectionTreeForWire = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map(stripCollectionTreeForWire) as T
  if (!isRecord(value)) return value
  const copy: Record<string, unknown> = {}
  for (const [key, child] of Object.entries(value)) {
    copy[key] =
      key === "variables" && Array.isArray(child)
        ? stripSecretVariableValuesForWire(child.filter(isRecord))
        : stripCollectionTreeForWire(child)
  }
  return copy as T
}

export const stripEnvironmentForWire = <
  T extends { variables?: Record<string, unknown>[] },
>(
  environment: T
): T => ({
  ...environment,
  ...(environment.variables
    ? { variables: stripSecretVariableValuesForWire(environment.variables) }
    : {}),
})

export const stripCollectionsForWire = (
  collections: HoppCollection[]
): HoppCollection[] => stripCollectionTreeForWire(collections)
